import React from 'react';
import { useAuth } from '../App';

export default function Welcome() {
  const { navigate } = useAuth();

  return (
    <div className="min-h-[calc(100vh-80px)]">
      {/* Hero Section */}
      <div className="bg-navy-900 py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="font-display text-4xl md:text-5xl text-white mb-4">
            Welcome to the Unlimited Luxury Leads Program
          </h1>
          <p className="text-xl text-navy-200 max-w-2xl mx-auto">
            Your exclusive portal for receiving high-quality luxury travel leads.
          </p>
        </div>
      </div>

      {/* Video Section */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="card-elevated">
          <h2 className="font-display text-2xl text-navy-900 mb-6 text-center">
            How It Works
          </h2>
          
          {/* Vimeo Embed */}
          <div className="relative w-full" style={{ paddingTop: '56.25%' }}>
            <iframe 
              src="https://player.vimeo.com/video/1171019949?badge=0&autopause=0&player_id=0&app_id=58479" 
              frameBorder="0" 
              allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share" 
              referrerPolicy="strict-origin-when-cross-origin"
              className="absolute top-0 left-0 w-full h-full rounded-lg"
              title="Lead Program Intro - Update"
            />
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="max-w-2xl mx-auto px-4 pb-16">
        <div className="grid md:grid-cols-2 gap-6">
          <div className="card text-center">
            <div className="w-16 h-16 bg-highlight-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-highlight-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
              </svg>
            </div>
            <h3 className="font-display text-xl text-navy-900 mb-2">New Advisor?</h3>
            <p className="text-navy-600 text-sm mb-4">
              Create your account to start receiving exclusive luxury travel leads.
            </p>
            <button 
              onClick={() => navigate('/register')} 
              className="btn-primary w-full"
            >
              Create Account
            </button>
          </div>

          <div className="card text-center">
            <div className="w-16 h-16 bg-navy-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-navy-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
              </svg>
            </div>
            <h3 className="font-display text-xl text-navy-900 mb-2">Existing Member?</h3>
            <p className="text-navy-600 text-sm mb-4">
              Sign in to your dashboard to view leads and manage your account.
            </p>
            <button 
              onClick={() => navigate('/login')} 
              className="btn-secondary w-full"
            >
              Sign In
            </button>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="bg-cream-50 py-16 px-4">
        <div className="max-w-5xl mx-auto">
          <h2 className="font-display text-2xl text-navy-900 text-center mb-10">
            Program Benefits
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-coral-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-coral-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <h3 className="font-display text-lg text-navy-900 mb-2">Pre-Qualified Leads</h3>
              <p className="text-navy-600 text-sm">
                Every lead is from someone actively seeking a luxury travel advisor.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-coral-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-coral-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="font-display text-lg text-navy-900 mb-2">Flexible Delivery</h3>
              <p className="text-navy-600 text-sm">
                Choose your delivery timeline from 2 to 8 weeks based on your capacity.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-coral-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-coral-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="font-display text-lg text-navy-900 mb-2">Instant Notifications</h3>
              <p className="text-navy-600 text-sm">
                Get email alerts the moment a new lead is assigned to you.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
